import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ArrowRight,
  Package,
  Award,
  Shield,
  Download,
  MessageCircle,
  Image as ImageIcon,
  FileText,
  Layers,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getSystems, captureLead, resolveMedia } from "@/lib/api";
import { toast } from "sonner";

const PLACEHOLDER_BG = "bg-gradient-to-br from-zinc-100 to-zinc-200";

const CATEGORY_COLORS = {
  asfalticos: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    accent: "bg-amber-600",
  },
  cementicios: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    accent: "bg-blue-600",
  },
  acrilicos: {
    bg: "bg-violet-50",
    border: "border-violet-200",
    text: "text-violet-700",
    accent: "bg-violet-600",
  },
  poliuretano: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    accent: "bg-emerald-600",
  },
};

function BeforeAfterSlider({ before, after, title }) {
  const [sliderPos, setSliderPos] = useState(50);
  const [dragging, setDragging] = useState(false);

  const handleMove = (e) => {
    if (!dragging) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));

    setSliderPos((x / rect.width) * 100);
  };

  return (
    <div
      data-testid="before-after-slider"
      className="relative w-full h-[360px] select-none cursor-col-resize overflow-hidden bg-zinc-100"
      onMouseDown={() => setDragging(true)}
      onMouseUp={() => setDragging(false)}
      onMouseLeave={() => setDragging(false)}
      onMouseMove={handleMove}
      onTouchStart={() => setDragging(true)}
      onTouchEnd={() => setDragging(false)}
      onTouchMove={handleMove}
    >
      <img
        src={resolveMedia(after)}
        alt={`${title} - Después`}
        className="absolute inset-0 w-full h-full object-cover"
      />

      <div
        className="absolute inset-0 overflow-hidden bg-zinc-100"
        style={{ width: `${sliderPos}%` }}
      >
        <img
          src={resolveMedia(before)}
          alt={`${title} - Antes`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            minWidth: "100%",
            width: `${100 / (sliderPos / 100)}%`,
            maxWidth: "none",
          }}
        />
      </div>

      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10"
        style={{ left: `${sliderPos}%` }}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
          <div className="flex gap-0.5">
            <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-transparent border-r-zinc-500" />
            <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-transparent border-l-zinc-500" />
          </div>
        </div>
      </div>

      <div className="absolute top-3 left-3 bg-zinc-900/70 text-white text-xs font-bold px-2 py-1 z-20">
        ANTES
      </div>

      <div className="absolute top-3 right-3 bg-emerald-600/90 text-white text-xs font-bold px-2 py-1 z-20">
        DESPUÉS
      </div>
    </div>
  );
}

function ProductCard({ product, index, colors, systemPdfSheet, onDownloadSheet }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.04 }}
      className={`border ${colors.border} bg-white overflow-hidden`}
      data-testid={`product-card-${index}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr]">
        <div className="bg-zinc-50 min-h-[210px] flex items-center justify-center border-b md:border-b-0 md:border-r border-zinc-100">
          {product.image_url ? (
            <img
              src={resolveMedia(product.image_url)}
              alt={product.name}
              className="w-full h-full max-h-[240px] object-contain p-4"
              data-testid={`product-image-${index}`}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-zinc-300 gap-2">
              <Package size={34} />
              <span className="text-xs text-zinc-400">Imagen pendiente</span>
            </div>
          )}
        </div>

        <div className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div
              className={`w-9 h-9 ${colors.bg} ${colors.border} border flex items-center justify-center shrink-0`}
            >
              <Package size={17} className={colors.text} />
            </div>

            <div>
              <h4 className="font-heading text-xl text-zinc-900">
                {product.name}
              </h4>

              {product.description && (
                <p className="text-zinc-600 text-sm leading-relaxed mt-1">
                  {product.description}
                </p>
              )}
            </div>
          </div>

          {product.advantages?.length > 0 && (
            <div>
              <h5 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                Ventajas
              </h5>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {product.advantages.map((advantage, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-zinc-600"
                  >
                    <CheckCircle2
                      size={14}
                      className="text-emerald-600 shrink-0 mt-0.5"
                    />
                    {advantage}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-3 text-xs text-zinc-500">
            {product.presentation && (
              <span
                className={`px-2 py-1 ${colors.bg} ${colors.text} ${colors.border} border`}
              >
                {product.presentation}
              </span>
            )}

            {product.yield_info && (
              <span
                className={`px-2 py-1 ${colors.bg} ${colors.text} ${colors.border} border`}
              >
                Rend: {product.yield_info}
              </span>
            )}
          </div>

          {systemPdfSheet && (
            <div className="pt-1">
              <Button
                type="button"
                onClick={() => onDownloadSheet(systemPdfSheet)}
                data-testid={`product-pdf-btn-${index}`}
                variant="outline"
                size="sm"
                className="rounded-none border-emerald-700 text-emerald-700 hover:bg-emerald-50 font-bold tracking-wide text-xs"
              >
                <Download size={13} className="mr-2" />
                FICHA DEL SISTEMA
              </Button>
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}

function SystemTopCard({ system, colors }) {
  const images = useMemo(
    () =>
      system.technical_images && system.technical_images.length > 0
        ? system.technical_images
        : system.image_url
        ? [system.image_url]
        : [],
    [system.technical_images, system.image_url]
  );
  const [imgIndex, setImgIndex] = useState(0);
  const hasImages = images.length > 0;
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    // Preload every gallery image so navigating with the arrows feels instant
    // instead of waiting for a fresh network fetch on each click.
    images.forEach((src) => {
      const preload = new window.Image();
      preload.src = resolveMedia(src);
    });
  }, [images]);

  const goPrevImg = () =>
    setImgIndex((current) => (current === 0 ? images.length - 1 : current - 1));
  const goNextImg = () =>
    setImgIndex((current) => (current === images.length - 1 ? 0 : current + 1));

  const technicalText =
    system.technical_description ||
    system.system_description ||
    system.long_description ||
    system.description ||
    "Párrafo técnico editable pendiente.";

  return (
    <div className="bg-white border border-zinc-200 overflow-hidden mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="border-b lg:border-b-0 lg:border-r border-zinc-100">

          <div className="relative h-[360px] bg-zinc-50 flex items-center justify-center">
            {hasImages ? (
              <img
                key={imgIndex}
                src={resolveMedia(images[imgIndex])}
                alt={system.title}
                className="w-full h-full object-contain p-5 animate-gallery-fade"
                data-testid={`system-image-${system.slug}`}
              />
            ) : (
              <div
                className={`${PLACEHOLDER_BG} w-full h-full flex flex-col items-center justify-center gap-3 text-zinc-300`}
              >
                <Shield size={40} />
                <span className="text-sm text-zinc-400">Imagen pendiente</span>
              </div>
            )}

            {hasMultipleImages && (
              <>
                <button
                  type="button"
                  onClick={goPrevImg}
                  data-testid={`system-image-prev-${system.slug}`}
                  aria-label="Imagen anterior"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 hover:bg-white border border-zinc-200 flex items-center justify-center transition-colors"
                >
                  <ChevronLeft size={18} className="text-zinc-700" />
                </button>
                <button
                  type="button"
                  onClick={goNextImg}
                  data-testid={`system-image-next-${system.slug}`}
                  aria-label="Imagen siguiente"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 hover:bg-white border border-zinc-200 flex items-center justify-center transition-colors"
                >
                  <ChevronRight size={18} className="text-zinc-700" />
                </button>
                <span className="absolute bottom-2 right-2 text-[11px] bg-zinc-900/70 text-white px-2 py-0.5">
                  {imgIndex + 1} / {images.length}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="p-6 lg:p-8 flex flex-col justify-center">
          <div className={`inline-flex w-fit items-center gap-2 px-3 py-1.5 ${colors.bg} ${colors.border} border text-xs font-bold ${colors.text} uppercase tracking-wider mb-4`}>
            <FileText size={13} />
            Descripción técnica
          </div>

          <h3 className="font-heading text-2xl text-zinc-900 mb-4">
            Información del sistema
          </h3>

          <p className="text-zinc-600 text-sm lg:text-base leading-relaxed whitespace-pre-line">
            {technicalText}
          </p>
        </div>
      </div>
    </div>
  );
}

function SystemInfoContent({ system, colors, index, systemPdfSheet, onDownloadSheet }) {
  return (
    <div className="p-6 lg:p-8 h-full flex flex-col">
      <div>
        <h3 className="font-bold text-zinc-900 uppercase text-xs tracking-wider mb-3">
          Ventajas del sistema
        </h3>

        {system.advantages?.length > 0 ? (
          <ul className="space-y-2">
            {system.advantages.map((advantage, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-zinc-600"
              >
                <CheckCircle2
                  size={14}
                  className="text-emerald-600 shrink-0 mt-0.5"
                />
                {advantage}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-zinc-400 text-sm">
            Contenido editable pendiente.
          </p>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-zinc-100">
        <h3 className="font-bold text-zinc-900 uppercase text-xs tracking-wider mb-3">
          Certificaciones
        </h3>

        {system.certifications?.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {system.certifications.map((certification, i) => (
              <Badge
                key={i}
                className={`rounded-none ${colors.bg} ${colors.text} ${colors.border} border`}
              >
                <Award size={12} className="mr-1" />
                {certification}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-zinc-400 text-sm">
            Contenido editable pendiente.
          </p>
        )}
      </div>

      <div className="mt-auto pt-6 border-t border-zinc-100 flex flex-wrap gap-3">
        <Link to="/contacto">
          <Button
            data-testid={`system-contact-btn-${index}`}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none px-6 font-bold tracking-wide"
          >
            CONSULTAR <ArrowRight className="ml-2" size={16} />
          </Button>
        </Link>

        {systemPdfSheet && (
          <Button
            type="button"
            onClick={() => onDownloadSheet(systemPdfSheet)}
            variant="outline"
            className="rounded-none border-emerald-700 text-emerald-700 hover:bg-emerald-50 px-6 font-bold tracking-wide"
          >
            <Download size={15} className="mr-2" />
            FICHA DEL SISTEMA
          </Button>
        )}
      </div>
    </div>
  );
}

function SystemBeforeAfterPanel({ system }) {
  const hasBothImages = Boolean(system.before_image && system.after_image);

  return (
    <div className="h-full border-t lg:border-t-0 lg:border-l border-zinc-100">
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2">
        <Shield size={16} className="text-emerald-700" />
        <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-600">
          Antes y después del sistema
        </h3>
      </div>

      {hasBothImages ? (
        <BeforeAfterSlider
          before={system.before_image}
          after={system.after_image}
          title={system.title}
        />
      ) : (
        <div className="h-[360px] bg-zinc-50 flex flex-col items-center justify-center gap-3 text-zinc-300">
          <ImageIcon size={38} />

          <p className="text-sm text-zinc-400 text-center px-8">
            Sube imagen ANTES y DESPUÉS desde el administrador para activar el
            comparador.
          </p>
        </div>
      )}
    </div>
  );
}

function SystemDetailsCard({ system, colors, index, systemPdfSheet, onDownloadSheet }) {
  return (
    <div className="bg-white border border-zinc-200 overflow-hidden mb-10">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <SystemInfoContent
          system={system}
          colors={colors}
          index={index}
          systemPdfSheet={systemPdfSheet}
          onDownloadSheet={onDownloadSheet}
        />

        <SystemBeforeAfterPanel system={system} />
      </div>
    </div>
  );
}

function SystemSection({ system, index, onDownloadSheet }) {
  const colors = CATEGORY_COLORS[system.slug] || CATEGORY_COLORS.asfalticos;

  const systemPdfSheet = system.system_pdf_url
    ? {
        id: system.id,
        title: `Ficha del sistema: ${system.title}`,
        file_url: system.system_pdf_url,
        source: "system_pdf",
      }
    : null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.1 }}
      className={`py-16 ${
        index % 2 === 0
          ? "bg-construtop-texture"
          : "bg-construtop-texture-soft"
      }`}
      data-testid={`system-section-${system.slug}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="mb-8">
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 ${colors.bg} ${colors.border} border text-xs font-bold ${colors.text} uppercase tracking-wider mb-4`}
          >
            <Shield size={12} />
            Sistema {String(index + 1).padStart(2, "0")}
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl text-zinc-900 mb-3">
            {system.title}
          </h2>

          <p className="text-zinc-600 leading-relaxed text-sm max-w-4xl">
            {system.description}
          </p>
        </div>

        <SystemTopCard system={system} colors={colors} />

        <SystemDetailsCard
          system={system}
          colors={colors}
          index={index}
          systemPdfSheet={systemPdfSheet}
          onDownloadSheet={onDownloadSheet}
        />

        <div>
          <h3 className="font-bold text-zinc-900 uppercase text-xs tracking-wider mb-4">
            Productos del sistema ({(system.products || []).length})
          </h3>

          <div className="space-y-4">
            {(system.products || []).map((product, i) => (
              <ProductCard
                key={i}
                product={product}
                index={i}
                colors={colors}
                systemPdfSheet={systemPdfSheet}
                onDownloadSheet={onDownloadSheet}
              />
            ))}

            {(!system.products || system.products.length === 0) && (
              <p className="text-zinc-400 text-sm italic py-4">
                Contenido en proceso de carga.
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

export default function SystemsPage() {
  const [systems, setSystems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [leadName, setLeadName] = useState("");
  const [leadWhatsapp, setLeadWhatsapp] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getSystems()
      .then((response) => setSystems(response.data || []))
      .catch(() => {});
  }, []);

  const orderedSystems = useMemo(
    () => [...systems].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [systems]
  );

  useEffect(() => {
    if (orderedSystems.length && activeIndex > orderedSystems.length - 1) {
      setActiveIndex(0);
    }
  }, [orderedSystems.length, activeIndex]);

  const activeSystem = orderedSystems[activeIndex];

  const goPrev = () => {
    if (!orderedSystems.length) return;
    setActiveIndex((current) =>
      current === 0 ? orderedSystems.length - 1 : current - 1
    );
  };

  const goNext = () => {
    if (!orderedSystems.length) return;
    setActiveIndex((current) =>
      current === orderedSystems.length - 1 ? 0 : current + 1
    );
  };

  const handleDownloadSheet = (sheet) => {
    setSelectedSheet(sheet);
    setDialogOpen(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    if (!leadName.trim() || !leadWhatsapp.trim()) {
      toast.error("Complete todos los campos");
      return;
    }

    const cleanNumber = leadWhatsapp.replace(/\D/g, "");

    if (cleanNumber.length < 8) {
      toast.error("Ingrese un número de WhatsApp válido");
      return;
    }

    setSubmitting(true);

    try {
      await captureLead({
        name: leadName,
        whatsapp: cleanNumber,
        sheet_id: selectedSheet?.id,
        source: "systems_page_system_pdf",
      });

      toast.success("Ficha técnica disponible para descarga");

      setDialogOpen(false);
      setLeadName("");
      setLeadWhatsapp("");

      const fileUrl = selectedSheet?.file_url;

      if (fileUrl && fileUrl !== "#") {
        window.open(resolveMedia(fileUrl), "_blank", "noopener");
      } else {
        toast.info(
          "Esta ficha aún no tiene PDF disponible. Nuestro equipo le contactará por WhatsApp."
        );
      }
    } catch {
      toast.error("Error al procesar. Intente de nuevo.");
    }

    setSubmitting(false);
  };

  return (
    <main
      data-testid="systems-page"
      className="pt-20 lg:pt-24 bg-construtop-texture"
    >
      <section className="bg-zinc-950 py-20 lg:py-28 section-padding relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-600/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-emerald-400 text-sm font-bold uppercase tracking-[0.2em] mb-3"
          >
            Sistemas de Impermeabilización
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-4xl sm:text-5xl text-white mb-4"
          >
            Sistemas completos de protección
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-zinc-400 max-w-xl text-base"
          >
            No vendemos productos aislados. Diseñamos sistemas integrales de
            impermeabilización con tecnología de clase mundial.
          </motion.p>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white sticky top-16 lg:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex flex-wrap gap-2">
          {orderedSystems.map((system, index) => (
            <button
              type="button"
              key={system.slug || system.id || index}
              onClick={() => setActiveIndex(index)}
              data-testid={`systems-filter-${system.slug}`}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-colors whitespace-nowrap ${
                activeIndex === index
                  ? "border-emerald-700 text-emerald-800 bg-emerald-50"
                  : "border-zinc-200 text-zinc-600 hover:border-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              }`}
            >
              {system.title}
            </button>
          ))}
        </div>
      </section>

      {activeSystem && (
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-8">
          <div className="mb-2 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3 text-sm text-zinc-500">
              <Layers size={18} className="text-emerald-700" />
              <span>
                Mostrando {activeIndex + 1} de {orderedSystems.length}: {activeSystem.title}
              </span>
              <span className="hidden sm:inline text-zinc-300">|</span>
              <span className="hidden sm:inline">
                {(activeSystem.products || []).length} productos disponibles
              </span>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={goPrev}
                className="rounded-none border-zinc-300 text-zinc-700"
              >
                <ChevronLeft size={16} className="mr-1" />
                ANTERIOR
              </Button>

              <Button
                type="button"
                onClick={goNext}
                className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                SIGUIENTE
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeSystem && (
        <SystemSection
          key={activeSystem.id}
          system={activeSystem}
          index={activeIndex}
          onDownloadSheet={handleDownloadSheet}
        />
      )}

      {!activeSystem && (
        <div className="py-20 text-center">
          <p className="text-zinc-400">No se encontraron sistemas.</p>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="systems-whatsapp-capture-dialog"
          className="sm:max-w-md rounded-none"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Descargar Ficha Técnica
            </DialogTitle>

            <DialogDescription>
              Ingrese sus datos para acceder a la descarga de:{" "}
              <strong>{selectedSheet?.title}</strong>
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleLeadSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="systems-lead-name">Nombre completo</Label>

              <Input
                data-testid="systems-lead-name-input"
                id="systems-lead-name"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                placeholder="Su nombre"
                className="rounded-none mt-1"
                required
              />
            </div>

            <div>
              <Label htmlFor="systems-lead-whatsapp">
                Número de WhatsApp
              </Label>

              <div className="relative mt-1">
                <MessageCircle
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#25D366]"
                />

                <Input
                  data-testid="systems-lead-whatsapp-input"
                  id="systems-lead-whatsapp"
                  type="tel"
                  value={leadWhatsapp}
                  onChange={(e) => setLeadWhatsapp(e.target.value)}
                  placeholder="+593 999 307 185"
                  className="rounded-none pl-10"
                  required
                />
              </div>

              <p className="text-xs text-zinc-400 mt-1">
                Incluya código de país. Ej: +593 para Ecuador.
              </p>
            </div>

            <Button
              data-testid="systems-lead-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-none font-bold"
            >
              <MessageCircle size={16} className="mr-2" />
              {submitting ? "PROCESANDO..." : "DESCARGAR FICHA"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}
